import {
    ALL_INSURANCE_PROVIDERS,
    ClientStatus,
    DateFormats,
    IClient,
    IFeesCalculated,
    InsuranceType,
} from "intake_shared/src/types";
import { Select } from "../../../../bits/Select/Select";
import { DatePicker } from "../../../../bits/DatePicker/DatePicker";
import moment from "../../../../../../utils/app_moment";
import { Button } from "../../../../bits/Button/Button";
import styles from "./InsuranceValues.module.scss";
import { Input } from "../../../../bits/Input/Input";
import { copyArrayToString, toTitleCase } from "../../../../../../utils/utils";
import CopyIcon from "../../../../icons/Copy";
import { cn } from "../../../../../helpers";
import CalculateFee from "../../fees/CalculateFee/CalculateFee";
import { Card } from "../../../../bits/Card/Card";
import FeesForm from "../../fees/FeesForm/FeesForm";
import {
    TUpdateClient,
    TUpdateMultipleClientFieldsByStatus,
} from "../../../clientUiTypes";
import { useToasts } from "react-toast-notifications";
import { FormCheckbox } from "../../../../bits/FormCheckbox/FormCheckbox";
import { TUpdateClientByStatus } from "../../../../../../types";
import {
    buildSelfPayAutoFillToastMessage,
    getSelfPayInsurancePatch,
} from "../../../../../v2utils";

const SELF_PAY_PROVIDER = "SELF-PAY";

interface InsuranceValuesProps {
    showLicFees: boolean;
    client: IClient;
    updateClientByStatus: TUpdateClientByStatus;
    updateClient: TUpdateClient;
    setFeesCalculated: React.Dispatch<
        React.SetStateAction<IFeesCalculated | undefined>
    >;
    setAutoFillFees: React.Dispatch<React.SetStateAction<boolean>>;
    feesCalculated?: IFeesCalculated | undefined;
    autoFillFees?: boolean;
    updateMultipleClientFieldsByStatus?: TUpdateMultipleClientFieldsByStatus;
    viewingAppointmentSetting?: boolean;
}

type Option = {
    label: React.ReactNode;
    value: string;
};

export default function InsuranceValues({
    showLicFees,
    client,
    updateClientByStatus,
    updateClient,
    setFeesCalculated,
    setAutoFillFees,
    feesCalculated,
    autoFillFees,
    updateMultipleClientFieldsByStatus,
    viewingAppointmentSetting = false,
}: InsuranceValuesProps) {
    const { addToast } = useToasts();

    const getInsuranceAcceptedOptions: Option[] = [
        {
            label: "Accepted",
            value: "Accepted",
        },
        {
            label: "Not Accepted",
            value: "Not Accepted",
        },
        {
            label: "Accepted Nat'l",
            value: "Accepted Nat'l",
        },
    ];

    const getInsuranceStatusOptions: Option[] = [
        {
            label: "Active",
            value: "Active",
        },
        {
            label: "Inactive",
            value: "Inactive",
        },
    ];

    const getInsuranceProviderOptions = ALL_INSURANCE_PROVIDERS.map((p) => ({
        label: p.toUpperCase(),
        value: p.toUpperCase(),
    }));

    const getInsuranceTypeOptions = Object.values(InsuranceType).map((p) => ({
        label: toTitleCase(p.replaceAll("_", " ")),
        value: p,
    }));

    const last_verification_date =
        client[ClientStatus.INS_VERIFICATION]?.last_verification_date;
    const momentLastVerified = last_verification_date
        ? moment(last_verification_date)
        : null;

    const handleUpdateEffectiveVerificationDate = () => {
        updateClientByStatus(
            ClientStatus.INS_VERIFICATION,
            "effective_date",
            moment(new Date()).format(DateFormats.DAY_MONTH_YEAR_FORMAT)
        );
    };

    const handleProviderChange = (rawValue: string) => {
        const nextProvider = rawValue.toUpperCase();
        updateClient("insurance_provider", nextProvider);
        if (
            nextProvider !== SELF_PAY_PROVIDER ||
            !updateMultipleClientFieldsByStatus
        ) {
            return;
        }
        const patch = getSelfPayInsurancePatch(client);
        if (Object.keys(patch).length === 0) {
            return;
        }
        updateMultipleClientFieldsByStatus(ClientStatus.INS_VERIFICATION, patch);
        const message = buildSelfPayAutoFillToastMessage(patch);
        if (message) {
            addToast(message, { appearance: "success", autoDismiss: true });
        }
    };

    return (
        <Card>
            <div className={styles.cardBody}>
                <p className={styles.sectionTitle}>Insurance & fee information</p>
                <form className="v2-form">
                    {viewingAppointmentSetting && (
                        <div className="gridThree">
                            {" "}
                            <Select
                                label="Insurance provider"
                                isSearchable
                                onChange={(val) =>
                                    handleProviderChange((val as Option).value)
                                }
                                options={[
                                    {
                                        label: "Select option",
                                        value: "",
                                    },
                                    ...getInsuranceProviderOptions,
                                ]}
                                defaultValue={getInsuranceProviderOptions.find(
                                    (item) =>
                                        item.value ===
                                        client.insurance_provider?.toUpperCase()
                                )}
                            />
                            <Input
                                label="Insurance member ID"
                                value={client.insurance_member_id || ""}
                                onChange={(e) => {
                                    updateClient(
                                        "insurance_member_id",
                                        e.target.value
                                    );
                                }}
                            />
                            <Select
                                label="Insurance type"
                                onChange={(val) =>
                                    updateClient(
                                        "insurance_type",
                                        (val as Option).value
                                    )
                                }
                                options={[
                                    { label: "Select option", value: "" },
                                    ...getInsuranceTypeOptions,
                                ]}
                                defaultValue={getInsuranceTypeOptions.find(
                                    (item) => item.value === client.insurance_type
                                )}
                            />
                        </div>
                    )}
                    <div className="gridThree">
                        <Input
                            label="Insurance plan employer"
                            value={client.insurance_plan_employer}
                            onChange={(e) => {
                                updateClient(
                                    "insurance_plan_employer",
                                    e.target.value
                                );
                            }}
                        />
                        <Input
                            label="Group Number"
                            value={client.group_number}
                            onChange={(e) => {
                                updateClient("group_number", e.target.value);
                            }}
                        />
                        <div className={styles.checkboxWrapper}>
                            <label className={styles.topLabel}>&nbsp;</label>

                            <FormCheckbox
                                label="Client is B2B"
                                checked={client.client_is_b2b}
                                onChange={() =>
                                    updateClient(
                                        "client_is_b2b",
                                        !client.client_is_b2b
                                    )
                                }
                            />
                        </div>
                    </div>
                    <div className="gridThree">
                        <Select
                            label="Insurance accepted"
                            onChange={(val) =>
                                updateClientByStatus(
                                    ClientStatus.INS_VERIFICATION,
                                    "insurance_accepted",
                                    (val as Option).value
                                )
                            }
                            options={[
                                { label: "Select option", value: "" },
                                ...getInsuranceAcceptedOptions,
                            ]}
                            defaultValue={getInsuranceAcceptedOptions.find(
                                (item) =>
                                    item.value ===
                                    client[ClientStatus.INS_VERIFICATION]
                                        ?.insurance_accepted
                            )}
                        />
                        <Select
                            label="Insurance status"
                            onChange={(val) =>
                                updateClientByStatus(
                                    ClientStatus.INS_VERIFICATION,
                                    "insurance_status",
                                    (val as Option).value
                                )
                            }
                            options={[
                                { label: "Select option", value: "" },
                                ...getInsuranceStatusOptions,
                            ]}
                            defaultValue={getInsuranceStatusOptions.find(
                                (item) =>
                                    item.value ===
                                    client[ClientStatus.INS_VERIFICATION]
                                        ?.insurance_status
                            )}
                        />

                        <DatePicker
                            dateFormat="MM/dd/yyyy"
                            label={
                                <span className={styles.dateLabelContainer}>
                                    <span>Last verified on</span>
                                    <Button
                                        variant="normal"
                                        size="small"
                                        onClick={() => {
                                            updateClientByStatus(
                                                ClientStatus.INS_VERIFICATION,
                                                "last_verification_date",
                                                new Date()
                                            );
                                        }}
                                        className={styles.updateButton}
                                    >
                                        Update
                                    </Button>
                                </span>
                            }
                            selected={
                                momentLastVerified
                                    ? momentLastVerified.toDate()
                                    : null
                            }
                            onChange={(date) =>
                                updateClientByStatus(
                                    ClientStatus.INS_VERIFICATION,
                                    "last_verification_date",
                                    date as Date
                                )
                            }
                        />
                    </div>
                    <div className={cn("v2-form", styles.fees)}>
                        <div className="gridFive">
                            <Input
                                label="INN Deductible"
                                value={
                                    client[ClientStatus.INS_VERIFICATION]?.INNDed ||
                                    ""
                                }
                                onChange={(e) => {
                                    updateClientByStatus(
                                        ClientStatus.INS_VERIFICATION,
                                        "INNDed",
                                        e.target.value
                                    );
                                }}
                            />
                            <Input
                                label="INN Copay"
                                value={
                                    client[ClientStatus.INS_VERIFICATION]?.INNCo ||
                                    ""
                                }
                                onChange={(e) => {
                                    updateClientByStatus(
                                        ClientStatus.INS_VERIFICATION,
                                        "INNCo",
                                        e.target.value
                                    );
                                }}
                            />
                            <Input
                                label="INN Out-of-pocket"
                                value={
                                    client[ClientStatus.INS_VERIFICATION]?.INNOOP ||
                                    ""
                                }
                                onChange={(e) => {
                                    updateClientByStatus(
                                        ClientStatus.INS_VERIFICATION,
                                        "INNOOP",
                                        e.target.value
                                    );
                                }}
                            />
                            <Input
                                label="INN Coinsurance"
                                value={
                                    client[ClientStatus.INS_VERIFICATION]
                                        ?.INNCoins || ""
                                }
                                onChange={(e) => {
                                    updateClientByStatus(
                                        ClientStatus.INS_VERIFICATION,
                                        "INNCoins",
                                        e.target.value
                                    );
                                }}
                            />
                            <div className={styles.copyButtonContainer}>
                                <Button
                                    variant="secondary"
                                    className={styles.copyButton}
                                    onClick={() => {
                                        copyArrayToString([
                                            client[ClientStatus.INS_VERIFICATION]
                                                ?.INNDed,
                                            client[ClientStatus.INS_VERIFICATION]
                                                ?.INNCo,
                                            client[ClientStatus.INS_VERIFICATION]
                                                ?.INNOOP,
                                            client[ClientStatus.INS_VERIFICATION]
                                                ?.INNCoins,
                                        ]);
                                        addToast("INN Copied to clipboard", {
                                            appearance: "success",
                                        });
                                    }}
                                    iconLeft={<CopyIcon />}
                                >
                                    Copy INN
                                </Button>
                            </div>
                        </div>

                        <div className="gridFive">
                            <Input
                                label="OON Deductible"
                                value={client[ClientStatus.INS_VERIFICATION]?.OONDed}
                                onChange={(e) => {
                                    updateClientByStatus(
                                        ClientStatus.INS_VERIFICATION,
                                        "OONDed",
                                        e.target.value
                                    );
                                }}
                            />
                            <Input
                                label="OON Copay"
                                value={client[ClientStatus.INS_VERIFICATION]?.OONCo}
                                onChange={(e) => {
                                    updateClientByStatus(
                                        ClientStatus.INS_VERIFICATION,
                                        "OONCo",
                                        e.target.value
                                    );
                                }}
                            />
                            <Input
                                label="OON Out-of-pocket"
                                value={client[ClientStatus.INS_VERIFICATION]?.OONOOP}
                                onChange={(e) => {
                                    updateClientByStatus(
                                        ClientStatus.INS_VERIFICATION,
                                        "OONOOP",
                                        e.target.value
                                    );
                                }}
                            />
                            <Input
                                label="OON Coinsurance"
                                value={
                                    client[ClientStatus.INS_VERIFICATION]?.OONCoins
                                }
                                onChange={(e) => {
                                    updateClientByStatus(
                                        ClientStatus.INS_VERIFICATION,
                                        "OONCoins",
                                        e.target.value
                                    );
                                }}
                            />
                            <div className={styles.copyButtonContainer}>
                                <Button
                                    variant="secondary"
                                    className={styles.copyButton}
                                    onClick={() => {
                                        copyArrayToString([
                                            client[ClientStatus.INS_VERIFICATION]
                                                ?.OONDed,
                                            client[ClientStatus.INS_VERIFICATION]
                                                ?.OONCo,
                                            client[ClientStatus.INS_VERIFICATION]
                                                ?.OONOOP,
                                            client[ClientStatus.INS_VERIFICATION]
                                                ?.OONCoins,
                                            client[ClientStatus.INS_VERIFICATION]
                                                ?.UCR,
                                            client[ClientStatus.INS_VERIFICATION]
                                                ?.CalYr,
                                            client[ClientStatus.INS_VERIFICATION]
                                                ?.accumulations_cal_yr,
                                        ]);
                                        addToast("OON Copied to clipboard", {
                                            appearance: "success",
                                        });
                                    }}
                                    iconLeft={<CopyIcon />}
                                >
                                    Copy OON
                                </Button>
                            </div>
                        </div>
                        <div className={styles.feesGrid}>
                            <Input
                                label="UCR"
                                value={client[ClientStatus.INS_VERIFICATION]?.UCR}
                                onChange={(e) => {
                                    updateClientByStatus(
                                        ClientStatus.INS_VERIFICATION,
                                        "UCR",
                                        e.target.value
                                    );
                                }}
                            />
                            <Input
                                label="Accumulations"
                                value={
                                    client[ClientStatus.INS_VERIFICATION]
                                        ?.accumulations_cal_yr
                                }
                                onChange={(e) => {
                                    updateClientByStatus(
                                        ClientStatus.INS_VERIFICATION,
                                        "accumulations_cal_yr",
                                        e.target.value
                                    );
                                }}
                            />
                            <Input
                                label="Policy Effective Date"
                                value={
                                    client[ClientStatus.INS_VERIFICATION]
                                        ?.policy_effective_date
                                }
                                onChange={(e) => {
                                    updateClientByStatus(
                                        ClientStatus.INS_VERIFICATION,
                                        "policy_effective_date",
                                        e.target.value
                                    );
                                }}
                            />
                            <Input
                                label={
                                    <span className={styles.dateLabelContainer}>
                                        <span>Effective Date</span>
                                        <Button
                                            variant="normal"
                                            size="small"
                                            onClick={
                                                handleUpdateEffectiveVerificationDate
                                            }
                                            className={styles.updateButton}
                                        >
                                            Update
                                        </Button>
                                    </span>
                                }
                                value={
                                    client[ClientStatus.INS_VERIFICATION]
                                        ?.effective_date || ""
                                }
                                onChange={(e) => {
                                    updateClientByStatus(
                                        ClientStatus.INS_VERIFICATION,
                                        "effective_date",
                                        e.target.value
                                    );
                                }}
                            />
                            <div className={styles.copyDateButtonContainer}>
                                <Button
                                    variant="secondary"
                                    iconLeft={<CopyIcon />}
                                    className={styles.copyButton}
                                    onClick={() => {
                                        copyArrayToString([
                                            client[ClientStatus.INS_VERIFICATION]
                                                ?.last_verification_date
                                                ? moment(
                                                      client[
                                                          ClientStatus
                                                              .INS_VERIFICATION
                                                      ]?.last_verification_date || ""
                                                  ).format(
                                                      DateFormats.DAY_MONTH_YEAR_FORMAT
                                                  )
                                                : "",
                                        ]);
                                    }}
                                >
                                    Copy verified date
                                </Button>
                            </div>
                            <div className={styles.calculateButton}>
                                <CalculateFee
                                    client={client}
                                    setFeesCalculated={setFeesCalculated}
                                    setAutoFillFees={setAutoFillFees}
                                />
                            </div>
                        </div>
                    </div>
                </form>
                <FeesForm
                    showLicFees={showLicFees}
                    client={client}
                    feesCalculated={feesCalculated}
                    autoFillFees={autoFillFees}
                    updateMultipleClientFieldsByStatus={
                        updateMultipleClientFieldsByStatus
                    }
                    updateClientByStatus={updateClientByStatus}
                    updateClient={updateClient}
                />
            </div>
        </Card>
    );
}
